import { createSlice } from '@reduxjs/toolkit';
import { Project } from '@shared/types/project.type';
import { ApiRequestStatus } from '@app/models/api/api-request-status.type';
import { ProjectCreateActions } from './project-create.actions';

interface WhitelistDraftEntry {
  key: string;
  regulatory_body: string[];
  justification: string;
}

interface WhitelistDraftState {
  projectId: string | null;
  selectedExistingRules: string[];
  existingRulesPayload: Record<
    string,
    {
      setA?: string[];
      setB?: string[];
      regulatory_body?: string[];
      justification?: string;
    }
  >;
  newWhitelistRules: WhitelistDraftEntry[];
}

interface createProjectState {
  projectData: Project | null;
  status: ApiRequestStatus;
  updateStatus: ApiRequestStatus;
  whitelistDraft: WhitelistDraftState;
}

const initialState: createProjectState = {
  projectData: null,
  status: 'idle',
  updateStatus: 'idle',
  whitelistDraft: {
    projectId: null,
    selectedExistingRules: [],
    existingRulesPayload: {},
    newWhitelistRules: []
  }
};

export const createProjectSlice = createSlice({
  name: 'createProject',
  initialState: initialState,
  reducers: {},
  extraReducers: builder =>
    builder
      .addCase(ProjectCreateActions.createProjectAttempt, state => ({
        ...state,
        projectData: null,
        status: 'loading'
      }))
      .addCase(ProjectCreateActions.createProjectSuccess, (state, action) => ({
        ...state,
        status: 'succeeded',
        projectData: action.payload.projectData
      }))
      .addCase(ProjectCreateActions.createProjectFailed, state => ({
        ...state,
        status: 'failed'
      }))

      // Update Project Actions
      .addCase(ProjectCreateActions.updateProjectAttempt, state => ({
        ...state,
        updateStatus: 'loading' // Set status to loading during update attempt
      }))
      .addCase(ProjectCreateActions.updateProjectSuccess, (state, action) => ({
        ...state,
        updateStatus: 'succeeded', // Set status to succeeded when update is successful
        projectData: action.payload.projectData // Update the project data with new details
      }))
      .addCase(ProjectCreateActions.updateProjectFailed, state => ({
        ...state,
        updateStatus: 'failed' // Set status to failed on update failure
      }))
      .addCase(ProjectCreateActions.resetCreateProjectState, () => ({
        ...initialState
      }))
      .addCase(ProjectCreateActions.setActiveProject, (state, action) => ({
        ...state,
        projectData: action.payload.projectData,
        whitelistDraft: {
          projectId: action.payload.projectData?.projectId ?? null,
          selectedExistingRules: [],
          existingRulesPayload: {},
          newWhitelistRules: []
        }
        // Don't set status to avoid triggering navigation in CreateNew
      }))
      .addCase(ProjectCreateActions.updateWhitelistDraft, (state, action) => ({
        ...state,
        whitelistDraft: {
          projectId: action.payload.projectId ?? state.whitelistDraft.projectId,
          selectedExistingRules: action.payload.selectedExistingRules,
          existingRulesPayload: action.payload.existingRulesPayload,
          newWhitelistRules: action.payload.newWhitelistRules
        }
      }))
});
